import { ConnectError } from '@bufbuild/connect';
import { useCallback, useState } from 'react';

import { useAppDispatch } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { errorToastCaller } from '@/shared/ui';

import { albumModel } from '@/entities/album';

type UsePhotoRemovalParams = {
  albumID: string;
  studyIDs: string[] | undefined;
  photoIDs: string[] | undefined;
  currentIndex: number;
  setCurrentIndex: (updater: (prev: number) => number) => void;
  onClose: () => void;
};

export const usePhotoRemoval = (params: UsePhotoRemovalParams) => {
  const {
    albumID,
    studyIDs,
    photoIDs,
    currentIndex,
    setCurrentIndex,
    onClose,
  } = params;

  const dispatch = useAppDispatch();

  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [targetPhotoIDForRemoval, setTargetPhotoIDForRemoval] = useState<
    string | null
  >(null);
  const [targetStudyIDForRemoval, setTargetStudyIDForRemoval] = useState<
    string | null
  >(null);
  const [targetIndexForRemoval, setTargetIndexForRemoval] = useState<
    number | null
  >(null);

  const handleOpenRemoveModal = useCallback(() => {
    if (!studyIDs || !photoIDs) {
      return;
    }

    const targetStudyID = studyIDs[currentIndex];
    const targetPhotoID = photoIDs[currentIndex];

    if (targetStudyID && targetPhotoID) {
      setTargetStudyIDForRemoval(targetStudyID);
      setTargetPhotoIDForRemoval(targetPhotoID);
      setTargetIndexForRemoval(currentIndex);
      setIsRemoveModalOpen(true);
    }
  }, [studyIDs, photoIDs, currentIndex]);

  const handleCloseRemoveModal = useCallback(() => {
    setIsRemoveModalOpen(false);
    setTargetPhotoIDForRemoval(null);
    setTargetStudyIDForRemoval(null);
    setTargetIndexForRemoval(null);
  }, []);

  const handleRemove = useCallback(async () => {
    if (
      !targetStudyIDForRemoval ||
      !targetPhotoIDForRemoval ||
      targetIndexForRemoval === null
    ) {
      return;
    }

    const targetStudyID = targetStudyIDForRemoval;
    const targetPhotoID = targetPhotoIDForRemoval;

    const wasLastElement =
      photoIDs && targetIndexForRemoval === photoIDs.length - 1;

    trackEvent({
      eventName: 'DentalPhotosOpenedPhotoRemoveClicked',
      properties: {
        photo_id: targetPhotoID,
        album_id: albumID,
      },
    });

    try {
      const { DentalPhotoAlbum } = await dispatch(
        albumModel.thunks.deleteStudiesFromAlbum({
          AlbumID: albumID,
          StudyIDs: [targetStudyID],
        }),
      ).unwrap();

      handleCloseRemoveModal();

      if (photoIDs && photoIDs.length === 1) {
        const { DeletedAlbumID } = await dispatch(
          albumModel.thunks.deleteAlbum({ AlbumID: albumID }),
        ).unwrap();

        if (DeletedAlbumID) {
          dispatch(albumModel.actions.removeOne(DeletedAlbumID));
        }

        onClose();
      } else if (DentalPhotoAlbum) {
        dispatch(albumModel.actions.setNewestOne(DentalPhotoAlbum));

        if (wasLastElement) {
          setCurrentIndex((prev) => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      errorToastCaller(error as ConnectError);
    }
  }, [
    targetStudyIDForRemoval,
    targetPhotoIDForRemoval,
    targetIndexForRemoval,
    photoIDs,
    albumID,
    dispatch,
    handleCloseRemoveModal,
    setCurrentIndex,
    onClose,
  ]);

  return {
    isRemoveModalOpen,
    handleOpenRemoveModal,
    handleCloseRemoveModal,
    handleRemove,
  };
};
